import { useState, useEffect, useRef } from 'react';
import {
  Lock, Save, Eye, EyeOff, CheckCircle, Clock, XCircle,
  Globe, Phone, Shield, Bell, Key, FileText,
  ExternalLink, AlertTriangle, Building2, User, MapPin,
  RefreshCw, SendHorizonal, Target, Paperclip, Upload, X, Image as ImageIcon, Trash2, Loader2,
  Copy, Check, RotateCcw, Download, Fingerprint, History, Stamp
} from 'lucide-react';
import { PrivacySettings } from '@consent-ui';
import QRCodeSVG from '@/components/QRCodeSVG';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useSTNAuth } from '@/context/STNAuthContext';
import {
  updateFounderProfile, updateStartupProfile,
  submitChangeRequest as apiSubmitChangeRequest,
  listChangeRequests as apiListChangeRequests,
  changePassword as apiChangePassword,
  uploadChangeDoc, uploadPhoto as apiUploadPhoto,
  getNotifPreferences, updateNotifPreferences,
  getSealInfo as apiGetSealInfo,
  generateSealKeys as apiGenerateSealKeys,
  uploadSealImage as apiUploadSealImage,
} from '@/api/stnApi';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STAGES   = ['Pre-idea', 'Ideation', 'MVP', 'Pre-Revenue', 'Revenue Stage', 'Growth', 'Scale'];
const SIZES    = ['Just me', '2–5', '6–10', '11–25', '26–50', '51–100', '100+'];
const ROLES    = ['Founder', 'Co-Founder', 'CEO', 'CTO', 'COO', 'CFO', 'Other'];
const CO_TYPES = ['Private Limited', 'LLP', 'OPC', 'Partnership', 'Sole Proprietorship', 'Public Limited', 'Section 8'];

const NOTIF_DEFAULTS = [
  { id: 'new_applications',  label: 'New Applications',      desc: 'Get notified when someone applies to your roles',   value: true  },
  { id: 'intern_milestones', label: 'Intern Milestones',     desc: 'Weekly milestone updates from active interns',      value: true  },
  { id: 'ai_match_digest',   label: 'AI Match Digest',       desc: 'Daily digest of new AI-matched candidates',         value: true  },
  { id: 'payment_confirm',   label: 'Payment Confirmations', desc: 'Receipt for every successful payment',              value: true  },
  { id: 'event_registrations', label: 'Event Registrations', desc: 'When someone registers for your events',           value: false },
  { id: 'sms_alerts',        label: 'SMS Alerts (Pro only)', desc: 'Critical alerts via SMS',                           value: false },
];

const FIELD_LABELS = {
  companyName:      'Company Name',
  companyType:      'Company Type',
  cin:              'CIN',
  gst:              'GST Number',
  pan:              'PAN',
  dpiit:            'DPIIT / Startup India Number',
  founded:          'Founded Year',
  registeredAddress:'Registered Address',
};

function LockedInput({ label, value, fieldKey, onRequest }) {
  return (
    <div>
      <Label className="flex items-center gap-1.5 mb-1">
        {label}
        <Lock className="w-3 h-3 text-muted-foreground" />
      </Label>
      <div className="flex gap-2">
        <Input value={value || '—'} disabled className="flex-1 bg-muted/40 text-foreground cursor-not-allowed" />
        <Button variant="outline" size="sm" className="shrink-0 text-xs gap-1 h-9" onClick={() => onRequest(fieldKey)}>
          <SendHorizonal className="w-3 h-3" /> Request Change
        </Button>
      </div>
    </div>
  );
}

function VerifiedBadge({ verified, verifiedAt }) {
  if (verified) return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-full">
      <CheckCircle className="w-3 h-3" /> Verified {verifiedAt ? `· ${new Date(verifiedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}` : ''}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground bg-muted border border-border px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" /> Not Verified
    </span>
  );
}

function StatusBanner({ status, notes }) {
  const cfg = {
    verified:       { cls: 'bg-success/10 border-success/25 text-success',     icon: CheckCircle,     text: 'Your startup is verified. Legal identifiers are locked to protect your verified identity.' },
    pending_review: { cls: 'bg-yellow-500/10 border-yellow-500/25 text-yellow-600', icon: Clock,      text: 'Your application is under review. Legal fields are temporarily locked.' },
    rejected:       { cls: 'bg-destructive/10 border-destructive/25 text-destructive', icon: XCircle, text: `Application rejected${notes ? ': ' + notes : ''}. Please update and resubmit.` },
    draft:          { cls: 'bg-muted border-border text-muted-foreground',      icon: AlertTriangle,   text: 'Complete your profile and submit for verification.' },
  };
  const c = cfg[status] || cfg.draft;
  const Icon = c.icon;
  return (
    <div className={cn('flex items-start gap-2.5 px-4 py-3 rounded-xl border text-sm', c.cls)}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{c.text}</span>
    </div>
  );
}

function RequestStatusBadge({ status }) {
  if (status === 'approved') return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-full"><CheckCircle className="w-2.5 h-2.5"/>Approved</span>;
  if (status === 'rejected') return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-destructive bg-destructive/10 border border-destructive/20 px-2 py-0.5 rounded-full"><XCircle className="w-2.5 h-2.5"/>Rejected</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold text-yellow-600 bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 rounded-full"><Clock className="w-2.5 h-2.5"/>Pending</span>;
}

// ── Download helper (used by verify modal) ────────────────────────────────────

function downloadSvg(svgString, filename) {
  const blob = new Blob([svgString], { type: 'image/svg+xml' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function downloadPng(urlOrDataUrl, filename) {
  let href = urlOrDataUrl;
  let objectUrl = null;
  if (urlOrDataUrl && !urlOrDataUrl.startsWith('data:')) {
    try {
      const res = await fetch(urlOrDataUrl);
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      href = objectUrl;
    } catch {
      // fallback: open in new tab
      window.open(urlOrDataUrl, '_blank');
      return;
    }
  }
  const a = document.createElement('a');
  a.href = href; a.download = filename; a.click();
  if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

// Remove near-white background from an image file → returns transparent PNG data-URL
function removeWhiteBackground(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        // Pass 1: sample corner pixel as the background colour
        const bgR = data[0], bgG = data[1], bgB = data[2];
        const threshold = 40; // colour distance to treat as "background"
        for (let i = 0; i < data.length; i += 4) {
          const dr = data[i]   - bgR;
          const dg = data[i+1] - bgG;
          const db = data[i+2] - bgB;
          if (Math.sqrt(dr*dr + dg*dg + db*db) < threshold) data[i+3] = 0;
        }
        ctx.putImageData(new ImageData(data, canvas.width, canvas.height), 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) { reject(e); } finally { URL.revokeObjectURL(objectUrl); }
    };
    img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
    img.src = objectUrl;
  });
}

export default function Settings() {
  const { founder, startup, refreshStartup } = useSTNAuth();
  const [logoUploading, setLogoUploading] = useState(false);

  // ── Seal state ──────────────────────────────────────────────────────────────
  const [sealInfo, setSealInfo]           = useState(null);
  const [sealLoading, setSealLoading]     = useState(false);
  const [sealGenLoading, setSealGenLoading] = useState(false);
  const [showSecret, setShowSecret]       = useState(false);
  const [copiedKey, setCopiedKey]         = useState('');  // 'key' | 'secret' | ''
  const [verifyModal, setVerifyModal]     = useState(false);
  const [regenConfirm, setRegenConfirm]   = useState(false);
  const [sealUploading, setSealUploading] = useState(false);

  // Persist seal image + local upload log in localStorage so they survive refresh
  const SEAL_IMG_KEY  = 'stn-seal-img';
  const SEAL_LOG_KEY  = 'stn-seal-local-log';
  const [sealImageUrl, setSealImageUrl] = useState(() => {
    try { return localStorage.getItem(SEAL_IMG_KEY) || ''; } catch { return ''; }
  });
  const [sealLocalLog, setSealLocalLog] = useState(() => {
    try { return JSON.parse(localStorage.getItem(SEAL_LOG_KEY) || '[]'); } catch { return []; }
  });

  function persistSealImage(url) {
    setSealImageUrl(url);
    try {
      if (url) localStorage.setItem(SEAL_IMG_KEY, url);
      else localStorage.removeItem(SEAL_IMG_KEY);
    } catch { /* storage full */ }
  }

  function pushSealLocalLog(entry) {
    setSealLocalLog(prev => {
      const next = [entry, ...prev].slice(0, 30);
      try { localStorage.setItem(SEAL_LOG_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }

  // ── Company logo upload (square, ≤2MB recommended; stored as logoUrl) ────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image (PNG, JPG, SVG, or WebP).'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Logo must be 2MB or smaller.'); return; }
    setLogoUploading(true);
    try {
      const up = await apiUploadPhoto(file);
      const url = up?.url || up?.res?.url;
      if (!url) throw new Error('Upload failed');
      await updateStartupProfile({ logoUrl: url });
      await refreshStartup();
      toast.success('Logo updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not upload logo.');
    } finally { setLogoUploading(false); }
  };
  const removeLogo = async () => {
    setLogoUploading(true);
    try { await updateStartupProfile({ logoUrl: '' }); await refreshStartup(); toast.success('Logo removed.'); }
    catch (err) { toast.error(err?.message || 'Could not remove logo.'); }
    finally { setLogoUploading(false); }
  };

  // ── Derived lock states ─────────────────────────────────────────────────────
  const cinLocked         = !!startup?.verification?.cin?.verified;
  const gstLocked         = !!startup?.verification?.gst?.verified;
  const panLocked         = !!startup?.verification?.pan?.verified;
  const isVerified        = startup?.status === 'verified';
  const isPending         = startup?.status === 'pending_review';
  const companyNameLocked = cinLocked || isVerified;
  const companyTypeLocked = cinLocked || isVerified;
  const cinFieldLocked    = cinLocked;
  const gstFieldLocked    = gstLocked;
  const panFieldLocked    = panLocked;
  const dpiitLocked       = isVerified || isPending;
  const foundedLocked     = isVerified;
  const addrLocked        = isVerified;

  // ── Profile form ────────────────────────────────────────────────────────────
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', role: 'Founder' });
  const [profileSaving, setProfileSaving] = useState(false);

  // ── Company form ────────────────────────────────────────────────────────────
  const [companyForm, setCompanyForm] = useState({
    website: '', contactPhone: '', contactEmail: '', sector: '', stage: '', teamSize: '', goals: [], premisesDescription: '',
    operationalAddress: { line1: '', line2: '', city: '', state: '', pincode: '' },
  });
  const [companySaving, setCompanySaving] = useState(false);
  const [goalInput, setGoalInput] = useState('');

  // ── Change requests ─────────────────────────────────────────────────────────
  const [changeReqs, setChangeReqs] = useState([]);
  const [reqsLoading, setReqsLoading] = useState(false);
  const [reqModal, setReqModal] = useState(null); // { fieldKey, label, currentValue }
  const [reqForm, setReqForm] = useState({ requestedValue: '', reason: '' });
  const [reqSaving, setReqSaving] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState(null); // { url, key, name }
  const docInputRef = useRef(null);

  // ── Security form ───────────────────────────────────────────────────────────
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({ current: false, newPw: false, confirm: false });

  // ── Notifications ───────────────────────────────────────────────────────────
  const [notifs, setNotifs] = useState(NOTIF_DEFAULTS);
  const [notifSaving, setNotifSaving] = useState(false);

  // Load real, feature-gated notification categories from the backend.
  useEffect(() => {
    getNotifPreferences()
      .then((p) => { if (Array.isArray(p?.categories) && p.categories.length) setNotifs(p.categories); })
      .catch(() => {});
  }, []);

  const saveNotifPrefs = async () => {
    setNotifSaving(true);
    try {
      const categories = {};
      notifs.forEach((n) => { categories[n.id] = !!n.value; });
      await updateNotifPreferences({ categories });
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e?.message || 'Could not save preferences');
    } finally { setNotifSaving(false); }
  };

  // ── Hydrate forms from context ──────────────────────────────────────────────
  useEffect(() => {
    if (founder) {
      setProfileForm({ name: founder.name || '', phone: founder.phone || '', role: founder.role || 'Founder' });
    }
  }, [founder]);

  useEffect(() => {
    if (startup) {
      setCompanyForm({
        website:             startup.website       || '',
        contactPhone:        startup.contactPhone  || '',
        contactEmail:        startup.contactEmail  || '',
        sector:              startup.sector        || '',
        stage:               startup.stage         || '',
        teamSize:            startup.teamSize || '',
        goals:               startup.goals   || [],
        premisesDescription: startup.premisesDescription || '',
        operationalAddress: {
          line1:   startup.operationalAddress?.line1   || '',
          line2:   startup.operationalAddress?.line2   || '',
          city:    startup.operationalAddress?.city    || '',
          state:   startup.operationalAddress?.state   || '',
          pincode: startup.operationalAddress?.pincode || '',
        },
      });
    }
  }, [startup]);

  useEffect(() => {
    setReqsLoading(true);
    apiListChangeRequests()
      .then(d => setChangeReqs(d.changeRequests || []))
      .catch(() => {})
      .finally(() => setReqsLoading(false));
  }, []);

  useEffect(() => {
    setSealLoading(true);
    apiGetSealInfo()
      .then(d => setSealInfo(d))
      .catch(() => {})
      .finally(() => setSealLoading(false));
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function handleSaveProfile() {
    setProfileSaving(true);
    try {
      await updateFounderProfile({ name: profileForm.name, phone: profileForm.phone, role: profileForm.role });
      await refreshStartup();
      toast.success('Profile updated successfully');
    } catch (e) { toast.error(e.message); }
    finally { setProfileSaving(false); }
  }

  async function handleSaveCompany() {
    setCompanySaving(true);
    try {
      await updateStartupProfile({
        website:             companyForm.website,
        contactPhone:        companyForm.contactPhone,
        contactEmail:        companyForm.contactEmail,
        sector:              companyForm.sector,
        stage:               companyForm.stage,
        teamSize:            companyForm.teamSize,
        goals:               companyForm.goals,
        premisesDescription: companyForm.premisesDescription,
        operationalAddress:  companyForm.operationalAddress,
      });
      await refreshStartup();
      toast.success('Company info saved');
    } catch (e) { toast.error(e.message); }
    finally { setCompanySaving(false); }
  }

  async function handleGenerateSealKeys() {
    setSealGenLoading(true);
    setRegenConfirm(false);
    try {
      const res = await apiGenerateSealKeys();
      setSealInfo(prev => ({
        ...(prev || {}),
        sealKey:      res.sealKey,
        sealSecretKey: res.sealSecretKey,
        sealCreatedAt: res.sealCreatedAt,
        sealAuditLog: [
          { action: res.action, actorName: founder?.name || '', at: res.sealCreatedAt },
          ...((prev?.sealAuditLog || []).slice(0, 19)),
        ],
      }));
      toast.success(res.action === 'created' ? 'Seal keys generated.' : 'Seal keys regenerated.');
    } catch (e) {
      toast.error(e.message || 'Could not generate seal keys.');
    } finally { setSealGenLoading(false); }
  }

  function copyToClipboard(text, which) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(which);
      setTimeout(() => setCopiedKey(''), 2000);
    });
  }

  async function handleSealUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSealUploading(true);
    try {
      const transparent = await removeWhiteBackground(file);
      // Save to localStorage immediately for local preview
      persistSealImage(transparent);
      // Also save to backend (S3) so it flows into InternNexus certificates
      try {
        const result = await apiUploadSealImage(transparent);
        const serverUrl = result?.url || result?.res?.url || '';
        if (serverUrl) {
          // Store the S3 URL too so the backend can use it directly
          persistSealImage(serverUrl);
        }
      } catch {
        // Backend upload failed — local preview still works, certificates will use localStorage fallback
      }
      pushSealLocalLog({
        type: 'seal-img',
        action: sealImageUrl ? 'replaced' : 'uploaded',
        fileName: file.name,
        actorName: founder?.name || '',
        at: new Date().toISOString(),
      });
      toast.success('Seal uploaded and saved — will appear on certificates.');
    } catch {
      toast.error('Could not process seal image. Please try a different file.');
    } finally {
      setSealUploading(false);
      e.target.value = '';
    }
  }

  function handleRemoveSeal() {
    persistSealImage('');
    pushSealLocalLog({
      type: 'seal-img',
      action: 'removed',
      fileName: '',
      actorName: founder?.name || '',
      at: new Date().toISOString(),
    });
  }

  async function handleDocUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(true);
    try {
      const res = await uploadChangeDoc(file);
      setUploadedDoc({ url: res.url, key: res.key, name: file.name });
      toast.success('Document uploaded');
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setDocUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  }

  function openRequestModal(fieldKey) {
    const currentValue = fieldKey === 'companyName' ? startup?.companyName
      : fieldKey === 'companyType'  ? startup?.companyType
      : fieldKey === 'cin'          ? startup?.cin
      : fieldKey === 'gst'          ? startup?.gst
      : fieldKey === 'pan'          ? startup?.pan
      : fieldKey === 'dpiit'        ? startup?.dpiit
      : fieldKey === 'founded'      ? startup?.founded
      : fieldKey === 'registeredAddress' ? [
          startup?.registeredAddress?.line1, startup?.registeredAddress?.city,
          startup?.registeredAddress?.state, startup?.registeredAddress?.pincode,
        ].filter(Boolean).join(', ')
      : '';
    setReqForm({ requestedValue: '', reason: '' });
    setUploadedDoc(null);
    setReqModal({ fieldKey, label: FIELD_LABELS[fieldKey] || fieldKey, currentValue: currentValue || '—' });
  }

  async function handleSubmitRequest() {
    if (!reqForm.requestedValue.trim()) { toast.error('Enter the new value'); return; }
    setReqSaving(true);
    try {
      await apiSubmitChangeRequest({
        field:          reqModal.fieldKey,
        requestedValue: reqForm.requestedValue.trim(),
        reason:         reqForm.reason.trim(),
        supportDocUrl:  uploadedDoc?.url  || "",
        supportDocKey:  uploadedDoc?.key  || "",
      });
      const d = await apiListChangeRequests();
      setChangeReqs(d.changeRequests || []);
      setReqModal(null);
      toast.success('Change request submitted. Admin will review shortly.');
    } catch (e) { toast.error(e.message); }
    finally { setReqSaving(false); }
  }

  async function handleChangePassword() {
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { toast.error('Fill all password fields'); return; }
    if (pwForm.newPw !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
    if (pwForm.newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setPwSaving(true);
    try {
      await apiChangePassword({ currentPassword: pwForm.current, newPassword: pwForm.newPw });
      setPwForm({ current: '', newPw: '', confirm: '' });
      toast.success('Password updated successfully');
    } catch (e) { toast.error(e.message); }
    finally { setPwSaving(false); }
  }

  function addGoal() {
    const g = goalInput.trim();
    if (!g || companyForm.goals.includes(g)) { setGoalInput(''); return; }
    setCompanyForm(p => ({ ...p, goals: [...p.goals, g] }));
    setGoalInput('');
  }

  function removeGoal(g) {
    setCompanyForm(p => ({ ...p, goals: p.goals.filter(x => x !== g) }));
  }

  const patchAddr = (key, val) =>
    setCompanyForm(p => ({ ...p, operationalAddress: { ...p.operationalAddress, [key]: val } }));

  const initials = founder?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??';
  const pendingReqs = changeReqs.filter(r => r.status === 'pending').length;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your profile, company details, and preferences</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-muted flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="profile" className="text-xs">Profile</TabsTrigger>
          <TabsTrigger value="company" className="text-xs">Company</TabsTrigger>
          <TabsTrigger value="requests" className="text-xs relative">
            Requests
            {pendingReqs > 0 && (
              <span className="ml-1.5 text-[9px] bg-yellow-500 text-white font-bold px-1.5 py-0.5 rounded-full">{pendingReqs}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="security" className="text-xs">Security</TabsTrigger>
          <TabsTrigger value="notifications" className="text-xs">Notifications</TabsTrigger>
          <TabsTrigger value="privacy" className="text-xs">Privacy</TabsTrigger>
        </TabsList>

        {/* ── Profile ─────────────────────────────────────────────────────── */}
        <TabsContent value="profile" className="mt-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-primary"/>Personal Profile</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center text-xl font-bold text-primary-foreground flex-shrink-0">
                  {initials}
                </div>
                <div>
                  <p className="font-semibold text-foreground">{founder?.name || '—'}</p>
                  <p className="text-sm text-muted-foreground">{founder?.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{founder?.stnId}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input className="mt-1" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">Email Address <Lock className="w-3 h-3 text-muted-foreground"/></Label>
                  <Input className="mt-1 bg-muted/40 cursor-not-allowed" value={founder?.email || ''} disabled />
                  <p className="text-[11px] text-muted-foreground mt-1">Email changes require admin approval. Use the Requests tab.</p>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"/>
                    <Input className="pl-8" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210"/>
                  </div>
                </div>
                <div>
                  <Label>Your Role</Label>
                  <Select value={profileForm.role} onValueChange={v => setProfileForm(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <Button className="gradient-primary text-primary-foreground border-0 gap-2" onClick={handleSaveProfile} disabled={profileSaving}>
                {profileSaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                {profileSaving ? 'Saving…' : 'Save Profile'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Company ─────────────────────────────────────────────────────── */}
        <TabsContent value="company" className="mt-6 space-y-4">

          {/* Status banner */}
          {startup?.status && <StatusBanner status={startup.status} notes={startup.verificationNotes} />}

          {/* Company Logo */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><ImageIcon className="w-4 h-4 text-primary"/>Company Logo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-5 flex-wrap">
                <div className="w-24 h-24 rounded-2xl border border-border bg-muted/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {startup?.logoUrl
                    ? <img src={startup.logoUrl} alt="Company logo" className="w-full h-full object-contain" />
                    : <ImageIcon className="w-8 h-8 text-muted-foreground/40" />}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className={cn('inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium cursor-pointer border border-border hover:bg-muted', logoUploading && 'opacity-60 pointer-events-none')}>
                      {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {startup?.logoUrl ? 'Replace logo' : 'Upload logo'}
                      <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} disabled={logoUploading} />
                    </label>
                    {startup?.logoUrl && (
                      <button onClick={removeLogo} disabled={logoUploading} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-destructive border border-border hover:bg-destructive/5 disabled:opacity-60">
                        <Trash2 className="w-4 h-4" /> Remove
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recommended: <b>square</b> (1:1), at least 256×256px. PNG, JPG, SVG, or WebP · max 2MB. A transparent PNG looks best.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Seal & Digital Identity */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Stamp className="w-4 h-4 text-primary"/>Company Seal &amp; Digital Identity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* Seal image upload */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Company Seal Image
                </p>

                {sealImageUrl ? (
                  /* Preview of seal */
                  <div className="flex flex-col items-center gap-3">
                    <div className="rounded-xl border border-border shadow-sm p-4 bg-white" style={{ minWidth: 160, minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img
                        src={sealImageUrl}
                        alt="Company Seal"
                        style={{ maxWidth: 200, maxHeight: 200, width: 'auto', height: 'auto', display: 'block', objectFit: 'contain' }}
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap justify-center">
                      <label className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors cursor-pointer">
                        <Upload className="w-3.5 h-3.5"/>
                        Replace
                        <input type="file" accept="image/*" className="hidden" onChange={handleSealUpload}/>
                      </label>
                      <button
                        onClick={() => downloadPng(sealImageUrl, `${(startup?.companyName || 'company').replace(/\s+/g, '-').toLowerCase()}-seal.png`)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                      >
                        <Download className="w-3.5 h-3.5"/> Download PNG
                      </button>
                      <button
                        onClick={handleRemoveSeal}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/5 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center">
                      Background removed automatically. Checkerboard = transparent area.
                    </p>
                  </div>
                ) : (
                  /* Upload dropzone */
                  <label className="flex flex-col items-center justify-center gap-3 w-full rounded-xl border-2 border-dashed border-border hover:border-primary/50 hover:bg-primary/3 transition-colors cursor-pointer py-10 px-4">
                    {sealUploading ? (
                      <>
                        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin"/>
                        <p className="text-sm text-muted-foreground">Removing background…</p>
                      </>
                    ) : (
                      <>
                        <div className="w-12 h-12 rounded-full bg-primary/8 flex items-center justify-center">
                          <Upload className="w-5 h-5 text-primary"/>
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-medium text-foreground">Upload your company seal</p>
                          <p className="text-xs text-muted-foreground mt-0.5">PNG, JPG or SVG · Background will be made transparent</p>
                        </div>
                      </>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={handleSealUpload} disabled={sealUploading}/>
                  </label>
                )}
              </div>

              {/* Seal Keys */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Seal Authentication Keys</p>
                  {sealInfo?.sealCreatedAt && (
                    <span className="text-[10px] text-muted-foreground">
                      Last generated {new Date(sealInfo.sealCreatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {sealLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading keys…
                  </div>
                ) : sealInfo?.sealKey ? (
                  <div className="space-y-2">
                    {/* Public key */}
                    <div>
                      <Label className="text-xs">Seal Public Key</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={sealInfo.sealKey}
                          readOnly
                          className="flex-1 font-mono text-sm bg-muted/40 cursor-default select-all"
                        />
                        <button
                          onClick={() => copyToClipboard(sealInfo.sealKey, 'key')}
                          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-xs"
                        >
                          {copiedKey === 'key' ? <Check className="w-3.5 h-3.5 text-success"/> : <Copy className="w-3.5 h-3.5"/>}
                          {copiedKey === 'key' ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    {/* Secret key */}
                    <div>
                      <Label className="text-xs">Seal Secret Key <span className="text-muted-foreground font-normal">(treat like a password — keep private)</span></Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={showSecret ? sealInfo.sealSecretKey : '•'.repeat(40)}
                          readOnly
                          className="flex-1 font-mono text-sm bg-muted/40 cursor-default"
                          type={showSecret ? 'text' : 'password'}
                        />
                        <button
                          onClick={() => setShowSecret(p => !p)}
                          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-xs"
                          title={showSecret ? 'Hide secret' : 'Reveal secret'}
                        >
                          {showSecret ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
                        </button>
                        <button
                          onClick={() => copyToClipboard(sealInfo.sealSecretKey, 'secret')}
                          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-xs"
                        >
                          {copiedKey === 'secret' ? <Check className="w-3.5 h-3.5 text-success"/> : <Copy className="w-3.5 h-3.5"/>}
                          {copiedKey === 'secret' ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button
                        onClick={() => setVerifyModal(true)}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                      >
                        <Fingerprint className="w-3.5 h-3.5 text-primary"/> Verify / View Certificate
                      </button>
                      {!regenConfirm ? (
                        <button
                          onClick={() => setRegenConfirm(true)}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border border-border hover:bg-muted text-muted-foreground transition-colors"
                        >
                          <RotateCcw className="w-3.5 h-3.5"/> Regenerate Keys
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 p-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 text-xs text-yellow-700 dark:text-yellow-400">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0"/>
                          Old keys will be invalidated.
                          <button onClick={handleGenerateSealKeys} disabled={sealGenLoading} className="font-semibold underline">
                            {sealGenLoading ? 'Regenerating…' : 'Confirm'}
                          </button>
                          <button onClick={() => setRegenConfirm(false)} className="ml-1">✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      No seal keys generated yet. Generate your first key pair to authenticate your company seal on documents.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={handleGenerateSealKeys}
                      disabled={sealGenLoading}
                    >
                      {sealGenLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Key className="w-4 h-4"/>}
                      {sealGenLoading ? 'Generating…' : 'Generate Seal Keys'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Company Identity — locked fields */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary"/>Company Identity
                {(companyNameLocked || cinFieldLocked) && (
                  <span className="text-[10px] font-normal text-muted-foreground flex items-center gap-1 ml-auto">
                    <Lock className="w-3 h-3"/> Some fields locked — use Request Change
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {companyNameLocked
                  ? <LockedInput label="Company Name" value={startup?.companyName} fieldKey="companyName" onRequest={openRequestModal}/>
                  : <div><Label>Company Name</Label><Input className="mt-1" value={startup?.companyName || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed"/></div>
                }
                {companyTypeLocked
                  ? <LockedInput label="Company Type" value={startup?.companyType} fieldKey="companyType" onRequest={openRequestModal}/>
                  : <div>
                      <Label>Company Type</Label>
                      <Input className="mt-1" value={startup?.companyType || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed" placeholder="Not set"/>
                    </div>
                }
                {foundedLocked
                  ? <LockedInput label="Founded Year" value={startup?.founded} fieldKey="founded" onRequest={openRequestModal}/>
                  : <div>
                      <Label>Founded Year</Label>
                      <Input className="mt-1" value={startup?.founded || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed" placeholder="Not set"/>
                    </div>
                }
                <div>
                  <Label>Website</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"/>
                    <Input className="pl-8" value={companyForm.website} onChange={e => setCompanyForm(p => ({ ...p, website: e.target.value }))} placeholder="yourcompany.com"/>
                  </div>
                </div>
                <div>
                  <Label>Contact Phone <span className="text-xs text-muted-foreground ml-1">(shown on documents)</span></Label>
                  <Input className="mt-1" value={companyForm.contactPhone} onChange={e => setCompanyForm(p => ({ ...p, contactPhone: e.target.value }))} placeholder="+91 98765 43210"/>
                </div>
                <div>
                  <Label>Contact Email <span className="text-xs text-muted-foreground ml-1">(shown on documents)</span></Label>
                  <Input className="mt-1" type="email" value={companyForm.contactEmail} onChange={e => setCompanyForm(p => ({ ...p, contactEmail: e.target.value }))} placeholder="contact@yourcompany.com"/>
                </div>
                <div>
                  <Label>Primary Sector</Label>
                  <Input className="mt-1" value={companyForm.sector} onChange={e => setCompanyForm(p => ({ ...p, sector: e.target.value }))} placeholder="AI/ML, SaaS, Fintech…"/>
                </div>
                <div>
                  <Label>Stage</Label>
                  <Select value={companyForm.stage || ''} onValueChange={v => setCompanyForm(p => ({ ...p, stage: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select stage"/></SelectTrigger>
                    <SelectContent>{STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Team Size</Label>
                  <Select value={companyForm.teamSize || ''} onValueChange={v => setCompanyForm(p => ({ ...p, teamSize: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select size"/></SelectTrigger>
                    <SelectContent>{SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Legal Identifiers */}
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-primary"/>Legal Identifiers</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1.5">CIN {cinFieldLocked && <Lock className="w-3 h-3 text-muted-foreground"/>}</Label>
                    <VerifiedBadge verified={cinLocked} verifiedAt={startup?.verification?.cin?.verifiedAt}/>
                  </div>
                  {cinFieldLocked
                    ? <div className="flex gap-2">
                        <Input value={startup?.cin || '—'} disabled className="flex-1 bg-muted/40 cursor-not-allowed font-mono text-sm"/>
                        <Button variant="outline" size="sm" className="shrink-0 text-xs gap-1 h-9" onClick={() => openRequestModal('cin')}>
                          <SendHorizonal className="w-3 h-3"/> Request Change
                        </Button>
                      </div>
                    : <Input value={startup?.cin || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed font-mono text-sm" placeholder="Not set"/>
                  }
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1.5">GST Number {gstFieldLocked && <Lock className="w-3 h-3 text-muted-foreground"/>}</Label>
                    <VerifiedBadge verified={gstLocked} verifiedAt={startup?.verification?.gst?.verifiedAt}/>
                  </div>
                  {gstFieldLocked
                    ? <div className="flex gap-2">
                        <Input value={startup?.gst || '—'} disabled className="flex-1 bg-muted/40 cursor-not-allowed font-mono text-sm"/>
                        <Button variant="outline" size="sm" className="shrink-0 text-xs gap-1 h-9" onClick={() => openRequestModal('gst')}>
                          <SendHorizonal className="w-3 h-3"/> Request Change
                        </Button>
                      </div>
                    : <Input value={startup?.gst || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed font-mono text-sm" placeholder="Not set"/>
                  }
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1.5">PAN {panFieldLocked && <Lock className="w-3 h-3 text-muted-foreground"/>}</Label>
                    <VerifiedBadge verified={panLocked} verifiedAt={startup?.verification?.pan?.verifiedAt}/>
                  </div>
                  {panFieldLocked
                    ? <div className="flex gap-2">
                        <Input value={startup?.pan || '—'} disabled className="flex-1 bg-muted/40 cursor-not-allowed font-mono text-sm"/>
                        <Button variant="outline" size="sm" className="shrink-0 text-xs gap-1 h-9" onClick={() => openRequestModal('pan')}>
                          <SendHorizonal className="w-3 h-3"/> Request Change
                        </Button>
                      </div>
                    : <Input value={startup?.pan || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed font-mono text-sm" placeholder="Not set"/>
                  }
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1.5">DPIIT / Startup India No. {dpiitLocked && <Lock className="w-3 h-3 text-muted-foreground"/>}</Label>
                    {startup?.dpiit && <span className="badge-success text-[10px]">DPIIT Registered</span>}
                  </div>
                  {dpiitLocked
                    ? <div className="flex gap-2">
                        <Input value={startup?.dpiit || '—'} disabled className="flex-1 bg-muted/40 cursor-not-allowed font-mono text-sm"/>
                        <Button variant="outline" size="sm" className="shrink-0 text-xs gap-1 h-9" onClick={() => openRequestModal('dpiit')}>
                          <SendHorizonal className="w-3 h-3"/> Request Change
                        </Button>
                      </div>
                    : <Input value={startup?.dpiit || ''} disabled className="mt-1 bg-muted/40 cursor-not-allowed font-mono text-sm" placeholder="Not set"/>
                  }
                </div>
              </div>

              {/* Document links */}
              {(startup?.gstCertUrl || startup?.coiFileUrl || startup?.dippCertUrl) && (
                <div className="pt-3 border-t border-border space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uploaded Documents</p>
                  <div className="flex flex-wrap gap-2">
                    {startup?.coiFileUrl && (
                      <a href={startup.coiFileUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                        <FileText className="w-3.5 h-3.5 text-primary"/> Certificate of Incorporation <ExternalLink className="w-2.5 h-2.5 opacity-60"/>
                      </a>
                    )}
                    {startup?.gstCertUrl && (
                      <a href={startup.gstCertUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                        <FileText className="w-3.5 h-3.5 text-primary"/> GST Certificate <ExternalLink className="w-2.5 h-2.5 opacity-60"/>
                      </a>
                    )}
                    {startup?.dippCertUrl && (
                      <a href={startup.dippCertUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                        <FileText className="w-3.5 h-3.5 text-primary"/> DPIIT Certificate <ExternalLink className="w-2.5 h-2.5 opacity-60"/>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-primary"/>Address</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {/* Registered */}
              <div>
                <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                  Registered Address
                  {addrLocked && <Lock className="w-3 h-3 text-muted-foreground"/>}
                  {addrLocked && (
                    <Button variant="ghost" size="sm" className="h-auto py-0.5 px-2 text-xs" onClick={() => openRequestModal('registeredAddress')}>
                      <SendHorizonal className="w-3 h-3 mr-1"/> Request Change
                    </Button>
                  )}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={startup?.registeredAddress?.line1 || ''} disabled className="bg-muted/40 cursor-not-allowed" placeholder="Line 1"/>
                  <Input value={startup?.registeredAddress?.line2 || ''} disabled className="bg-muted/40 cursor-not-allowed" placeholder="Line 2"/>
                  <Input value={startup?.registeredAddress?.city  || ''} disabled className="bg-muted/40 cursor-not-allowed" placeholder="City"/>
                  <Input value={startup?.registeredAddress?.state || ''} disabled className="bg-muted/40 cursor-not-allowed" placeholder="State"/>
                  <Input value={startup?.registeredAddress?.pincode || ''} disabled className="bg-muted/40 cursor-not-allowed" placeholder="Pincode"/>
                </div>
              </div>

              {/* Operational */}
              <div>
                <p className="text-sm font-medium text-foreground mb-3">Operational Address</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input value={companyForm.operationalAddress.line1} onChange={e => patchAddr('line1', e.target.value)} placeholder="Line 1"/>
                  <Input value={companyForm.operationalAddress.line2} onChange={e => patchAddr('line2', e.target.value)} placeholder="Line 2"/>
                  <Input value={companyForm.operationalAddress.city}  onChange={e => patchAddr('city',  e.target.value)} placeholder="City"/>
                  <Input value={companyForm.operationalAddress.state} onChange={e => patchAddr('state', e.target.value)} placeholder="State"/>
                  <Input value={companyForm.operationalAddress.pincode} onChange={e => patchAddr('pincode', e.target.value)} placeholder="Pincode"/>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Goals */}
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Target className="w-4 h-4 text-primary"/>Startup Goals</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2 min-h-[36px]">
                {companyForm.goals.map(g => (
                  <span key={g} className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/10 border border-primary/20 text-primary px-2.5 py-1 rounded-full">
                    {g}
                    <button onClick={() => removeGoal(g)} className="hover:text-destructive transition-colors">×</button>
                  </span>
                ))}
                {companyForm.goals.length === 0 && <p className="text-xs text-muted-foreground">No goals added yet</p>}
              </div>
              <div className="flex gap-2">
                <Input
                  value={goalInput}
                  onChange={e => setGoalInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addGoal())}
                  placeholder="Add a goal and press Enter…"
                  className="flex-1"
                />
                <Button variant="outline" size="sm" onClick={addGoal} className="shrink-0">Add</Button>
              </div>
              <div>
                <Label>Company Description / Premises</Label>
                <Textarea className="mt-1" rows={3} value={companyForm.premisesDescription} onChange={e => setCompanyForm(p => ({ ...p, premisesDescription: e.target.value }))} placeholder="Brief description of your operations…"/>
              </div>
            </CardContent>
          </Card>

          {/* Identity Audit Log — always visible */}
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4 text-primary"/>Identity Audit Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const allEntries = [
                  ...(sealInfo?.sealAuditLog  || []).map(e => ({ ...e, type: 'seal-key' })),
                  ...(sealInfo?.logoAuditLog  || []).map(e => ({ ...e, type: 'logo' })),
                  ...sealLocalLog,
                ].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 40);

                if (allEntries.length === 0) {
                  return (
                    <div className="flex flex-col items-center gap-2 py-8 text-center">
                      <History className="w-8 h-8 text-muted-foreground/30"/>
                      <p className="text-sm text-muted-foreground">No activity yet.</p>
                      <p className="text-xs text-muted-foreground">Actions on your seal, logo, and keys will appear here.</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-0.5">
                    {allEntries.map((entry, i) => {
                      const isSealKey = entry.type === 'seal-key';
                      const isSealImg = entry.type === 'seal-img';
                      const isLogo    = entry.type === 'logo';

                      const iconBg = isSealKey ? 'bg-primary/10' : isSealImg ? 'bg-blue-500/10' : 'bg-muted';
                      const icon   = isSealKey
                        ? <Stamp    className="w-3.5 h-3.5 text-primary"/>
                        : isSealImg
                          ? <ImageIcon className="w-3.5 h-3.5 text-blue-500"/>
                          : <ImageIcon className="w-3.5 h-3.5 text-muted-foreground"/>;

                      const label = isSealKey
                        ? `Seal keys ${entry.action}`
                        : isSealImg
                          ? `Seal image ${entry.action}${entry.fileName ? ` (${entry.fileName})` : ''}`
                          : `Logo ${entry.action}`;

                      return (
                        <div key={i} className="flex items-start gap-3 py-2.5 border-b border-border/40 last:border-0">
                          <div className={cn('w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5', iconBg)}>
                            {icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground capitalize">{label}</p>
                            {entry.actorName && (
                              <p className="text-xs text-muted-foreground mt-0.5">by {entry.actorName}</p>
                            )}
                            {isLogo && entry.url && (
                              <p className="text-[10px] text-muted-foreground truncate mt-0.5">{entry.url}</p>
                            )}
                          </div>
                          <p className="text-[11px] text-muted-foreground shrink-0 mt-0.5">
                            {new Date(entry.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Button className="gradient-primary text-primary-foreground border-0 gap-2" onClick={handleSaveCompany} disabled={companySaving}>
            {companySaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
            {companySaving ? 'Saving…' : 'Save Company Info'}
          </Button>
        </TabsContent>

        {/* ── Change Requests ──────────────────────────────────────────────── */}
        <TabsContent value="requests" className="mt-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <SendHorizonal className="w-4 h-4 text-primary"/>Change Requests
                {pendingReqs > 0 && (
                  <span className="text-[10px] bg-yellow-500 text-white font-bold px-2 py-0.5 rounded-full">{pendingReqs} pending</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reqsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <RefreshCw className="w-4 h-4 animate-spin"/> Loading…
                </div>
              ) : changeReqs.length === 0 ? (
                <div className="text-center py-10">
                  <CheckCircle className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2"/>
                  <p className="text-sm text-muted-foreground">No change requests yet</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">Request changes to locked fields from the Company tab</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {changeReqs.map(req => (
                    <div key={String(req._id)} className={cn(
                      'p-4 rounded-xl border',
                      req.status === 'approved' ? 'border-success/20 bg-success/5'
                      : req.status === 'rejected' ? 'border-destructive/20 bg-destructive/5'
                      : 'border-yellow-500/20 bg-yellow-500/5'
                    )}>
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="font-semibold text-sm text-foreground">{FIELD_LABELS[req.field] || req.field}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(req.requestedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <RequestStatusBadge status={req.status}/>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="bg-background rounded-lg p-2 border border-border">
                          <p className="text-muted-foreground mb-0.5">Current</p>
                          <p className="font-mono font-medium text-foreground truncate">{req.currentValue || '—'}</p>
                        </div>
                        <div className="bg-background rounded-lg p-2 border border-border">
                          <p className="text-muted-foreground mb-0.5">Requested</p>
                          <p className="font-mono font-medium text-foreground truncate">{req.requestedValue}</p>
                        </div>
                      </div>
                      {req.reason && (
                        <p className="text-xs text-muted-foreground mt-2 italic">"{req.reason}"</p>
                      )}
                      {req.supportDocUrl && (
                        <a href={req.supportDocUrl} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs mt-2 text-primary hover:underline">
                          <Paperclip className="w-3 h-3"/> View support document
                        </a>
                      )}
                      {req.resolvedNote && (
                        <p className={cn('text-xs mt-2 font-medium', req.status === 'approved' ? 'text-success' : 'text-destructive')}>
                          Admin note: {req.resolvedNote}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Security ─────────────────────────────────────────────────────── */}
        <TabsContent value="security" className="mt-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4 text-primary"/>Security</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPw.current ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={pwForm.current}
                    onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                    className="pr-10"
                  />
                  <button onClick={() => setShowPw(p => ({ ...p, current: !p.current }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw.current ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
              </div>
              <div>
                <Label>New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPw.newPw ? 'text' : 'password'}
                    placeholder="Min 8 characters"
                    value={pwForm.newPw}
                    onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                    className="pr-10"
                  />
                  <button onClick={() => setShowPw(p => ({ ...p, newPw: !p.newPw }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw.newPw ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPw.confirm ? 'text' : 'password'}
                    placeholder="Repeat new password"
                    value={pwForm.confirm}
                    onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                    className={cn('pr-10', pwForm.confirm && pwForm.newPw && pwForm.confirm !== pwForm.newPw && 'border-destructive')}
                  />
                  <button onClick={() => setShowPw(p => ({ ...p, confirm: !p.confirm }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw.confirm ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                  </button>
                </div>
                {pwForm.confirm && pwForm.newPw && pwForm.confirm !== pwForm.newPw && (
                  <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                )}
              </div>
              <Button
                className="gradient-primary text-primary-foreground border-0 gap-2"
                onClick={handleChangePassword}
                disabled={pwSaving}
              >
                {pwSaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Key className="w-4 h-4"/>}
                {pwSaving ? 'Updating…' : 'Update Password'}
              </Button>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-foreground">Two-Factor Authentication</p>
                    <p className="text-xs text-muted-foreground">Add extra security via OTP — coming soon</p>
                  </div>
                  <Switch disabled/>
                </div>
              </div>

              {founder?.lastPassChange && (
                <p className="text-xs text-muted-foreground">
                  Last changed: {new Date(founder.lastPassChange).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Notifications ────────────────────────────────────────────────── */}
        <TabsContent value="notifications" className="mt-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4 text-primary"/>Notification Preferences</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {notifs.map(n => (
                <div key={n.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="font-medium text-sm text-foreground">{n.label}</p>
                    <p className="text-xs text-muted-foreground">{n.desc}</p>
                  </div>
                  <Switch
                    checked={n.value}
                    onCheckedChange={() => setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, value: !x.value } : x))}
                  />
                </div>
              ))}
              {notifs.length === 0 && <p className="text-sm text-muted-foreground py-4">No notification categories available for your current features.</p>}
              <Button
                className="gradient-primary text-primary-foreground border-0 gap-2 mt-2"
                disabled={notifSaving}
                onClick={saveNotifPrefs}
              >
                {notifSaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
                {notifSaving ? 'Saving…' : 'Save Preferences'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Privacy & consent ───────────────────────────────────────────────
            Shared component — the same screen SkillNexus, InternNexus and
            PromptNexus render, themed by AppConsentProvider. Shows what this
            founder has agreed to, lets them withdraw it, and lists the full
            append-only log of every grant and withdrawal with timestamps. */}
        <TabsContent value="privacy" className="mt-6">
          <Card className="border-border">
            <CardContent className="pt-6">
              <PrivacySettings />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Verify Seal / Certificate Modal ──────────────────────────────────── */}
      <Dialog open={verifyModal} onOpenChange={setVerifyModal}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-5 pb-0">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Fingerprint className="w-4 h-4 text-primary"/> Seal Verification Certificate
            </DialogTitle>
          </DialogHeader>

          {/* ── Certificate body ── */}
          {(() => {
            const shortKey = sealInfo?.sealKey
              ? sealInfo.sealKey.replace(/^NSK-/, '').replace(/-/g, '').slice(0, 8).toUpperCase()
              : '';
            const verifyUrl = sealInfo?.sealKey
              ? `https://nexusai.in/verify?key=${sealInfo.sealKey}`
              : 'https://nexusai.in/verify';
            const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
            const designation = founder?.designation || 'Internship Program Officer';

            return (
              <div className="mx-6 my-4 rounded-xl border border-border/70 bg-white overflow-hidden shadow-sm">
                {/* Header band */}
                <div className="bg-[#1e3a5f] px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-sm tracking-wide">{startup?.companyName || 'Your Company'}</p>
                    {startup?.cin && <p className="text-white/60 text-[10px] font-mono mt-0.5">CIN: {startup.cin}</p>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle className="w-4 h-4 text-green-400"/>
                    <span className="text-green-400 text-xs font-semibold">NexusAI Verified</span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">

                  {/* QR + code */}
                  <div className="flex items-start gap-5">
                    <div className="flex flex-col items-center gap-1.5 shrink-0">
                      <div className="border border-border rounded-lg p-1.5 bg-white shadow-sm">
                        <QRCodeSVG value={verifyUrl} size={88} color="#1e3a5f"/>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-medium">Scan to verify</p>
                    </div>

                    <div className="flex-1 space-y-3 pt-1">
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Verification Code</p>
                        <p className="font-mono text-xl font-bold text-[#1e3a5f] tracking-widest mt-0.5">{shortKey || '—'}</p>
                        {sealInfo?.sealKey && (
                          <p className="font-mono text-[9px] text-muted-foreground mt-0.5">{sealInfo.sealKey}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0">Designation</span>
                          <span className="text-foreground font-medium">{designation}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0">Date</span>
                          <span className="text-foreground">{today}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide w-24 shrink-0">Place</span>
                          <span className="text-foreground">India</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-border/60"/>

                  {/* Organization Seal */}
                  <div>
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">Organization Seal</p>
                    <div className="flex items-center gap-5">
                      {sealImageUrl ? (
                        <div className="shrink-0 rounded-xl border border-border bg-white p-2 shadow-sm" style={{ width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img
                            src={sealImageUrl}
                            alt="Organization Seal"
                            style={{ maxWidth: 108, maxHeight: 108, width: 'auto', height: 'auto', display: 'block', objectFit: 'contain' }}
                          />
                        </div>
                      ) : (
                        <div className="shrink-0 w-28 h-28 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 bg-muted/20">
                          <Stamp className="w-7 h-7 text-muted-foreground/30"/>
                          <p className="text-[9px] text-muted-foreground text-center px-1">No seal uploaded</p>
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          This seal is digitally registered with NexusAI and authenticates documents issued by{' '}
                          <strong className="text-foreground">{startup?.companyName || 'this organization'}</strong>.
                          Scan the QR code or enter the verification code at nexusai.in/verify to confirm authenticity.
                        </p>
                        {!sealImageUrl && (
                          <p className="text-[10px] text-amber-600 mt-2 font-medium">
                            → Upload your seal in the Company tab to display it here.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-muted/30 border-t border-border/50 px-5 py-2.5 flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">Powered by NexusAI · nexusai.in</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(sealInfo?.sealKey || '', 'key')}
                      className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded border border-border hover:bg-muted transition-colors"
                    >
                      {copiedKey === 'key' ? <Check className="w-3 h-3 text-success"/> : <Copy className="w-3 h-3"/>}
                      Copy Key
                    </button>
                    {sealImageUrl && (
                      <button
                        onClick={() => downloadPng(sealImageUrl, `${(startup?.companyName || 'company').replace(/\s+/g, '-').toLowerCase()}-seal.png`)}
                        className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1.5 rounded border border-border hover:bg-muted transition-colors"
                      >
                        <Download className="w-3 h-3"/> Download Seal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <DialogFooter className="px-6 pb-5">
            <Button variant="outline" onClick={() => setVerifyModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Change Request Modal ─────────────────────────────────────────────── */}
      <Dialog open={!!reqModal} onOpenChange={open => !open && setReqModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SendHorizonal className="w-4 h-4 text-primary"/>
              Request Change — {reqModal?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-muted border border-border">
              <p className="text-xs text-muted-foreground mb-1">Current Value</p>
              <p className="font-mono text-sm font-medium text-foreground">{reqModal?.currentValue || '—'}</p>
            </div>
            <div>
              <Label>Requested New Value <span className="text-destructive">*</span></Label>
              <Input
                className="mt-1"
                placeholder={`Enter new ${reqModal?.label?.toLowerCase()}…`}
                value={reqForm.requestedValue}
                onChange={e => setReqForm(p => ({ ...p, requestedValue: e.target.value }))}
              />
            </div>
            <div>
              <Label>Reason for Change</Label>
              <Textarea
                className="mt-1"
                rows={3}
                placeholder="Why do you need to change this? (e.g. company restructuring, correction)"
                value={reqForm.reason}
                onChange={e => setReqForm(p => ({ ...p, reason: e.target.value }))}
              />
            </div>
            {/* Supporting Document */}
            <div>
              <Label className="flex items-center gap-1.5">
                Supporting Document
                <span className="text-muted-foreground font-normal">(recommended for legal fields)</span>
              </Label>
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={handleDocUpload}
              />
              {uploadedDoc ? (
                <div className="mt-1 flex items-center gap-2 p-2.5 rounded-lg border border-success/30 bg-success/5">
                  <FileText className="w-4 h-4 text-success shrink-0"/>
                  <a href={uploadedDoc.url} target="_blank" rel="noopener noreferrer"
                    className="flex-1 text-xs text-success font-medium hover:underline truncate">
                    {uploadedDoc.name}
                  </a>
                  <button onClick={() => setUploadedDoc(null)} className="text-muted-foreground hover:text-destructive">
                    <X className="w-3.5 h-3.5"/>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  disabled={docUploading}
                  className="mt-1 w-full flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-border hover:border-primary/50 hover:bg-muted/50 transition-colors text-sm text-muted-foreground disabled:opacity-50"
                >
                  {docUploading
                    ? <><RefreshCw className="w-3.5 h-3.5 animate-spin"/> Uploading…</>
                    : <><Upload className="w-3.5 h-3.5"/> Upload PDF, JPG, or PNG (max 10 MB)</>
                  }
                </button>
              )}
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <AlertTriangle className="w-3.5 h-3.5 text-yellow-600 mt-0.5 shrink-0"/>
              <p className="text-xs text-yellow-700 dark:text-yellow-400">
                This request will be reviewed by the NexusAI admin team. Changes to verified legal identifiers may require supporting documents.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReqModal(null)} disabled={reqSaving}>Cancel</Button>
            <Button className="gradient-primary text-primary-foreground border-0 gap-2" onClick={handleSubmitRequest} disabled={reqSaving}>
              {reqSaving ? <RefreshCw className="w-4 h-4 animate-spin"/> : <SendHorizonal className="w-4 h-4"/>}
              {reqSaving ? 'Submitting…' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
